/**
 * Single source of truth for the currently-selected `{provider, model}` in the
 * AgentBar. Lifted out of AgentBar's local state so operation builders
 * (text-op + outline-gen) can read the same selection without prop-drilling
 * or passing callbacks. Mirrors the studio/metadata pattern in
 * StudioRequestContext but lives in a Zustand slice so it survives the
 * AgentBar's collapse/expand cycle and persists across studio switches
 * within a lab session.
 *
 * Persistence (`localStorage`): on refresh the user's last selection comes
 * back. Cleared explicitly when the user picks a new studio whose
 * `defaultLlmModel` differs — see `WriterLab.tsx` mount effect.
 *
 * NB: this is intentionally lab-agnostic. Image Lab still uses its
 * per-panel ProviderModelPicker (different surface); Writer's Lab uses
 * one bar-level pick that drives every text op. If Image Lab later wants
 * the same pattern, this store can be reused.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AgentModelSelection {
  provider: string;
  model: string;
}

interface AgentModelState {
  selection: AgentModelSelection | null;
  setSelection: (next: AgentModelSelection | null) => void;
  /** Set only if the current value is empty/null. Used by AgentBar's seed-from-studio effect. */
  setIfUnset: (next: AgentModelSelection) => void;
  clear: () => void;
}

export const useAgentModelStore = create<AgentModelState>()(
  persist(
    set => ({
      selection: null,
      setSelection: (next) => set({ selection: next }),
      setIfUnset: (next) =>
        set(state => (state.selection?.model ? state : { selection: next })),
      clear: () => set({ selection: null }),
    }),
    { name: 'ct_v3_agent_model_selection' },
  ),
);

/** Read-only hook for non-AgentBar consumers (mutation hooks, outline-gen). */
export function useSelectedModel(): string | undefined {
  return useAgentModelStore(s => s.selection?.model);
}

/** Non-hook accessor for places outside React (e.g. queryFn callbacks). */
export function readSelectedModel(): string | undefined {
  return useAgentModelStore.getState().selection?.model;
}
